import { DEFAULT_FONT_SIZE, type RichTextStyle } from "@library-elements/text";
import { getHighlightSpanInParents } from "../../../utils/highlight";
import { getLinkInParents } from "../../../utils/links";
import { TEXT_STYLE_TAGS, TEXT_STYLES } from "../constants";
import {
  ICON_SELECTOR,
  findHighlight,
  findRkpi,
  isIcon,
} from "../element-queries";
import { setPending } from "../pending-format-state";
import type { FroalaEditorInstance } from "../types";

/**
 * Apply a callback to special elements (RKPIs, highlights, icons) that
 * are within the current selection range only.
 */
const applyToSpecialElementsInRange = (
  editor: FroalaEditorInstance,
  sel: Selection,
  callback: (el: HTMLElement) => void,
) => {
  const blocks = editor.selection.blocks() as HTMLElement[];
  for (const b of Array.from(blocks)) {
    if (!(b instanceof HTMLElement)) continue;

    const rkpis = b.querySelectorAll("[data-rkpi-active]");
    for (const rkpi of Array.from(rkpis)) {
      if (rkpi instanceof HTMLElement && sel.containsNode(rkpi, true)) {
        callback(rkpi);
      }
    }

    const highlights = b.querySelectorAll("[data-highlight-active]");
    for (const hi of Array.from(highlights)) {
      if (hi instanceof HTMLElement && sel.containsNode(hi, true)) {
        callback(hi);
      }
    }

    const fontIcons = b.querySelectorAll(ICON_SELECTOR);
    for (const icon of Array.from(fontIcons)) {
      if (icon instanceof HTMLElement && sel.containsNode(icon, true)) {
        callback(icon);
      }
    }
  }
};

export const handleFontSize = (
  editor: FroalaEditorInstance,
  el: HTMLElement,
  data: number,
  handleAllSelection: () => boolean,
  refresh: (clearSelection?: boolean) => void,
) => {
  // DEFAULT_FONT_SIZE (0) means "drop the explicit size" so the text falls back
  // to the design system size for its style. An empty CSSOM value removes the
  // declaration; Froala's own format.removeStyle does the same for the spans it
  // owns (and unwraps them when nothing is left).
  const size = data > DEFAULT_FONT_SIZE ? `${data}px` : "";
  const applySize = (target: HTMLElement) =>
    target.style.setProperty("font-size", size);
  const applySizeToSelection = () =>
    size
      ? editor.format.applyStyle("font-size", size)
      : editor.format.removeStyle("font-size");

  // Collapsed cursor → select all and apply, or pending state for special elements
  if (editor.selection.isCollapsed()) {
    const hasHighlight = findHighlight(el);
    const hasRkpi = findRkpi(el);
    if (hasHighlight) {
      applySize(hasHighlight);
      editor.save.save();
    } else if (hasRkpi) {
      applySize(hasRkpi);
      editor.save.save();
    } else if (isIcon(el)) {
      applySize(el);
      editor.save.save();
    } else {
      // No special element — select all and apply font size to entire text
      // handleAllSelection() must be called before window.getSelection()
      // because it creates a new native selection via selectAll
      const isAllSelected = handleAllSelection();
      if (isAllSelected) {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          applyToSpecialElementsInRange(editor, sel, applySize);
        }
        applySizeToSelection();
        editor.save.save();
        refresh(isAllSelected);
        return;
      }
      setPending("fontSize", size);
    }
    refresh();
    return;
  }

  // Selection: apply to selected text + special elements in range only
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  let shouldUseFroala = true;
  const hasHighlight = findHighlight(el);
  const hasRkpi = findRkpi(el);
  if (hasHighlight || hasRkpi) {
    shouldUseFroala = false;
  }

  if (isIcon(el)) {
    applySize(el);
  }

  if (shouldUseFroala) {
    applyToSpecialElementsInRange(editor, sel, applySize);

    applySizeToSelection();
  } else {
    if (hasHighlight) {
      applySize(hasHighlight);
    } else if (hasRkpi) {
      applySize(hasRkpi);
    }
  }

  editor.save.save();
  refresh();
};

export const handleTextColor = (
  editor: FroalaEditorInstance,
  el: HTMLElement,
  data: string,
  handleAllSelection: () => boolean,
  refresh: (clearSelection?: boolean) => void,
) => {
  // Collapsed cursor
  if (editor.selection.isCollapsed()) {
    const hiSpan = getHighlightSpanInParents(el);
    if (el.getAttribute("data-rkpi-active")) {
      el.style.setProperty("color", data);
      editor.save.save();
    } else if (hiSpan) {
      hiSpan.style.setProperty("--var-highlight-text-color", data);
      editor.save.save();
    } else if (isIcon(el)) {
      el.style.setProperty("color", data);
      editor.save.save();
    } else {
      // No special element — select all and apply color to entire text
      // handleAllSelection() must be called before window.getSelection()
      // because it creates a new native selection via selectAll
      const isAllSelected = handleAllSelection();
      if (isAllSelected) {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          applyToSpecialElementsInRange(editor, sel, (specialEl) => {
            if (specialEl.hasAttribute("data-highlight-active")) {
              specialEl.style.setProperty("--var-highlight-text-color", data);
            } else {
              specialEl.style.setProperty("color", data);
            }
          });
        }
        editor.format.applyStyle("color", data);
        editor.save.save();
        refresh(isAllSelected);
        return;
      }
      setPending("color", data);
    }
    refresh();
    return;
  }

  // Selection: apply to selected range
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  const hiSpan = getHighlightSpanInParents(el);
  const linkEl = getLinkInParents(el);

  if (el.getAttribute("data-rkpi-active")) {
    el.style.setProperty("color", data);
  } else if (hiSpan) {
    hiSpan.style.setProperty("--var-highlight-text-color", data);
  } else if (
    isIcon(el) &&
    sel.getRangeAt(0).startContainer === sel.getRangeAt(0).endContainer
  ) {
    el.style.setProperty("color", data);
  } else if (linkEl) {
    linkEl.style.setProperty("color", data);
  } else {
    // Apply to special elements in the selection range only
    applyToSpecialElementsInRange(editor, sel, (specialEl) => {
      if (specialEl.hasAttribute("data-highlight-active")) {
        specialEl.style.setProperty("--var-highlight-text-color", data);
      } else {
        specialEl.style.setProperty("color", data);
      }
    });

    editor.format.applyStyle("color", data);
  }

  editor.save.save();
  refresh();
};

export const handleTextStyle = (
  editor: FroalaEditorInstance,
  data: RichTextStyle | null,
  handleAllSelection: () => boolean,
  refresh: (clearSelection?: boolean) => void,
) => {
  const isAllSelected = handleAllSelection();
  const sel = window.getSelection();
  if (!data || !sel || sel.rangeCount === 0) return;

  const vars = TEXT_STYLES[data];
  const blocks = editor.selection.blocks() as HTMLElement[];
  const newElTag = TEXT_STYLE_TAGS[data] ?? "p";

  editor.selection.save();
  if (vars) {
    for (const block of Array.from(blocks)) {
      const fontSizeEls = block.querySelectorAll('[style*="font-size"]');
      for (const e of Array.from(fontSizeEls)) {
        if (e instanceof HTMLElement) {
          e.style.removeProperty("font-size");
        }
      }

      if (block.tagName === "LI") {
        block.style.setProperty("font-size", `var(--${vars.size})`);
        block.style.setProperty("color", `var(--${vars.color})`);
        block.style.setProperty("font-family", `var(--${vars.font})`);
        block.style.setProperty("font-weight", `var(--${vars.weight})`);
      } else if (block.tagName !== newElTag.toUpperCase()) {
        block.style.removeProperty("font-size");
        block.style.removeProperty("line-height");
        const newEl = document.createElement(newElTag);
        for (let i = 0; i < block.attributes.length; i++) {
          const attribute = block.attributes[i];
          newEl.setAttribute(attribute.name, attribute.value);
        }
        while (block.firstChild) {
          newEl.appendChild(block.firstChild);
        }
        newEl.style.setProperty("font-size", `var(--${vars.size})`);
        newEl.style.setProperty("color", `var(--${vars.color})`);
        newEl.style.setProperty("font-family", `var(--${vars.font})`);
        newEl.style.setProperty("font-weight", `var(--${vars.weight})`);
        block.replaceWith(newEl);
      }
    }
  }
  editor.selection.restore();
  editor.save.save();
  refresh(isAllSelected);
};

export const handleLineHeight = (
  editor: FroalaEditorInstance,
  data: string,
  handleAllSelection: () => boolean,
  refresh: (clearSelection?: boolean) => void,
) => {
  const isAllSelected = handleAllSelection();
  const blocks = editor.selection.blocks() as HTMLElement[];
  for (const block of Array.from(blocks)) {
    block.style.setProperty("line-height", data);

    const els = block.querySelectorAll('[style*="line-height"]');
    for (const el of Array.from(els)) {
      if (el instanceof HTMLElement) {
        el.style.removeProperty("line-height");
      }
    }
  }
  editor.save.save();
  refresh(isAllSelected);
};
